import { semanticSearch } from '@/lib/semanticSearch';
import { supabase } from '@/integrations/supabase/client';
import { useLibrarianSearch } from '@/hooks/useLibrarianSearch';

type KnowledgeBaseLookupRow = {
  id: string;
  name: string;
};

export function useKnowledgeBaseSearch() {
  const { search: searchLibrarian } = useLibrarianSearch();

  const findKnowledgeBaseInContent = async (content: string): Promise<{ kbId: string | null; kbName: string | null }> => {
    const kbCommandMatch = content.match(/\/kb\s*:?\s*([a-zA-Z0-9-]+)/);
    if (kbCommandMatch?.[1]) {
      const kbId = kbCommandMatch[1];
      const { data } = await (supabase as any).from('knowledge_bases').select('name').eq('id', kbId).maybeSingle();
      return { kbId, kbName: (data as { name?: string } | null)?.name ?? null };
    }

    const hashtagMatch = content.match(/#([a-zA-Z0-9_-]+)/);
    if (!hashtagMatch?.[1]) {
      return { kbId: null, kbName: null };
    }

    const tagName = hashtagMatch[1];
    const { data } = await (supabase as any).from('knowledge_bases').select('id, name');
    const knowledgeBases = (data ?? []) as KnowledgeBaseLookupRow[];
    const matched = knowledgeBases.find((kb) => kb.name.toLowerCase() === tagName.toLowerCase()) ?? null;
    return { kbId: matched?.id ?? null, kbName: matched?.name ?? null };
  };

  const performKnowledgeBaseSearch = async (
    kbId: string,
    content: string,
    addSystemMessage: (content: string) => Promise<void>
  ) => {
    try {
      const { data: kb } = await (supabase as any).from('knowledge_bases').select('name').eq('id', kbId).single();
      const kbName = (kb as { name?: string } | null)?.name ?? null;
      const layeredResults = await searchLibrarian(kbId, content);

      if (layeredResults.length > 0) {
        await addSystemMessage(
          kbName
            ? `🔍 Searching knowledge base "${kbName}" with Librarian vault-first retrieval…`
            : '🔍 Searching knowledge base with Librarian vault-first retrieval…'
        );

        const contextContent = layeredResults
          .slice(0, 8)
          .map((result, index) => {
            const label = result.kind === 'vault'
              ? `Vault Page ${index + 1}: ${result.title}${result.path ? ` (${result.path})` : ''}`
              : `Source ${index + 1}: ${result.title}`;

            return `${label}\nSimilarity: ${(result.similarity * 100).toFixed(1)}%${result.retrievalReason ? `\nWhy included: ${result.retrievalReason}` : ''}\n${result.content.slice(0, 4000)}${result.content.length > 4000 ? '…' : ''}`;

          })
          .join('\n\n');

        return {
          found: true,
          empty: false,
          contextContent,
          kbName,
        };
      }

      const sourceFallback = await semanticSearch(kbId, content, { limit: 3, threshold: 0.15 });
      if (sourceFallback.length > 0) {
        const contextContent = sourceFallback
          .map((doc, index) => `Source ${index + 1}: ${doc.name}\n${doc.content.slice(0, 3000)}${doc.content.length > 3000 ? '…' : ''}`)
          .join('\n\n');

        return {
          found: true,
          empty: false,
          contextContent,
          kbName,
        };
      }

      const { data: documents } = await supabase
        .from('knowledge_base_documents')
        .select('id')
        .eq('knowledge_base_id', kbId)
        .limit(1);

      if (!documents || documents.length === 0) {
        await addSystemMessage(`⚠️ The knowledge base ${kbName ? `"${kbName}" ` : ''}has no source documents yet.`);
        return {
          found: true,
          empty: true,
          message: `The knowledge base ${kbName ? `"${kbName}" ` : ''}is empty. Ask the user to add source documents first.`,
          kbName,
        };
      }

      await addSystemMessage(`⚠️ No relevant Librarian vault pages or sources were found for this query.`);
      return {
        found: true,
        empty: false,
        noMatches: true,
        message: `No relevant Librarian vault pages or source documents matched the query "${content}". Tell the user the knowledge base may need more coverage or a fresh ingest.`,
        kbName,
      };
    } catch (error) {
      console.error('Error performing knowledge base search:', error);
      await addSystemMessage(`⚠️ Error searching the knowledge base: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return {
        found: false,
        error: true,
        message: `There was an error searching the knowledge base: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  };

  return {
    findKnowledgeBaseInContent,
    performKnowledgeBaseSearch,
  };
}